<template>
  <div
    class="fixed inset-0 flex flex-col"
    style="z-index: 65; background: rgba(0, 0, 0, 0.5)"
  >
    <div
      class="mt-auto bg-gray-100 rounded-t-lg max-h-screen overflow-y-auto pb-10"
    >
      <!-- Handle -->
      <div class="flex justify-center pt-3 pb-4">
        <div class="w-10 h-1 bg-gray-400 rounded-full"></div>
      </div>

      <div class="px-4 sm:px-6">
        <!-- Back link when inside a sub-form -->
        <button
          v-if="selectedReason"
          @click="selectedReason = null"
          class="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-3"
        >
          <unicon name="angle-left" class="w-4 h-4" fill="currentColor" />
          All issues
        </button>

        <div
          class="block uppercase tracking-wide text-gray-500 text-xs font-bold mb-1"
        >
          {{ productName }}
        </div>
        <div
          class="text-2xl sm:text-3xl font-bold text-gray-800 leading-tight mb-5"
        >
          {{ selectedReason ? currentReasonLabel : "What's the issue?" }}
        </div>

        <!-- Reason list -->
        <div v-if="!selectedReason" class="space-y-3">
          <div
            v-for="reason in reasons"
            :key="reason.key"
            @click="selectedReason = reason.key"
            class="p-4 sm:p-5 rounded-lg cursor-pointer transition-colors"
            :class="reason.bg"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex-1 min-w-0">
                <div class="text-base font-semibold" :class="reason.text">
                  {{ reason.label }}
                </div>
                <div class="text-sm mt-0.5 text-gray-500">
                  {{ reason.hint }}
                </div>
              </div>
              <div class="w-5 h-5 flex-shrink-0" :class="reason.icon">
                <unicon name="angle-right" fill="currentColor" />
              </div>
            </div>
          </div>

          <button
            @click="$emit('cancel')"
            class="w-full pt-4 text-sm text-gray-400 hover:text-gray-600 py-2"
          >
            Go back
          </button>
        </div>

        <!-- Wrong Number form -->
        <div v-if="selectedReason === 'wrong-qty'" class="space-y-4">
          <div class="p-4 rounded-lg bg-gray-200 text-sm text-gray-600">
            Expected:
            <strong>{{ expectedQty !== false ? expectedQty : '—' }}</strong>
            <span v-if="scanQuantity !== null">
              &nbsp;·&nbsp; Invoice shows: <strong>{{ scanQuantity }}</strong>
            </span>
          </div>
          <div>
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              How many received?
            </label>
            <input
              v-model.number="amountReceived"
              type="number"
              min="0"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 text-2xl font-bold text-center"
              placeholder="0"
            />
          </div>
          <button
            @click="save"
            :disabled="amountReceived === null"
            class="w-full py-3 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            Save
          </button>
        </div>

        <!-- Damaged / Poor Quality / Wrong Item form -->
        <div v-if="requiresPhotos" class="space-y-4">
          <!-- Wrong item: what was actually received -->
          <div v-if="selectedReason === 'wrong-item'">
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              What did you receive instead?
            </label>
            <input
              v-model="wrongItemDescription"
              type="text"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              placeholder="e.g. Chicken thighs instead of breasts"
            />
          </div>

          <div>
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Photo
              <span class="normal-case font-normal text-red-500"
                >(required)</span
              >
            </label>
            <div v-if="productPhotoUrl" class="mb-2">
              <img
                :src="productPhotoUrl"
                class="w-full max-h-40 object-cover rounded-lg"
              />
              <button
                @click="productPhotoUrl = null"
                class="mt-1 text-xs text-red-600 hover:text-red-800"
              >
                Remove
              </button>
            </div>
            <camera-capture
              v-else
              :is-developer-or-admin="isDeveloperOrAdmin"
              @captured="productPhotoUrl = $event"
            />
          </div>

          <div>
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              {{
                selectedReason === 'damaged'
                  ? 'How many affected?'
                  : 'How many were delivered?'
              }}
            </label>
            <input
              v-model.number="amountReturned"
              type="number"
              min="0"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 text-2xl font-bold text-center"
              placeholder="0"
            />
          </div>

          <div>
            <label
              class="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
            >
              Notes (optional)
            </label>
            <input
              v-model="returnReason"
              type="text"
              class="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded-lg py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              placeholder="e.g. Shipment arrived crushed"
            />
          </div>

          <button
            @click="save"
            :disabled="!productPhotoUrl"
            class="w-full py-3 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-40 transition-colors"
          >
            Save
          </button>
        </div>

        <!-- Not Received confirmation -->
        <div v-if="selectedReason === 'not-received'" class="space-y-4">
          <div class="p-4 rounded-lg bg-gray-200 text-sm text-gray-600">
            This item will be marked as <strong>not received</strong>.
          </div>
          <button
            @click="save"
            class="w-full py-3 px-6 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import CameraCapture from '@/components/CameraCapture'

export default {
  name: 'ReceivingRejectionModal',
  components: { CameraCapture },
  props: {
    productId: { type: String, required: true },
    invoiceId: { type: String, required: true },
    scanQuantity: { type: Number, default: null },
    isDeveloperOrAdmin: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedReason: null,
      amountReceived: null,
      amountReturned: 0,
      productPhotoUrl: null,
      returnReason: '',
      wrongItemDescription: ''
    }
  },
  computed: {
    ...mapGetters('storeModule/products', ['productById']),
    ...mapGetters('storeModule/invoices', ['invoiceById']),

    productName() {
      return (this.productById(this.productId) || {}).name || this.productId
    },

    expectedQty() {
      const inv = this.invoiceById(this.invoiceId)
      return inv && inv[this.productId]
        ? inv[this.productId].amountOrdered
        : false
    },

    reasons() {
      return [
        {
          key: 'wrong-qty',
          label: 'Wrong Number Received',
          hint: 'Different quantity than ordered',
          bg: 'bg-gray-300 hover:bg-gray-400 active:bg-gray-400',
          text: 'text-gray-800',
          icon: 'text-gray-500'
        },
        {
          key: 'damaged',
          label: 'Damaged / Poor Quality',
          hint: 'Item arrived damaged, broken, or unusable',
          bg: 'bg-gray-300 hover:bg-gray-400 active:bg-gray-400',
          text: 'text-red-600',
          icon: 'text-red-400'
        },
        {
          key: 'wrong-item',
          label: 'Wrong Item',
          hint: 'Received a substitute or incorrect product',
          bg: 'bg-gray-300 hover:bg-gray-400 active:bg-gray-400',
          text: 'text-gray-800',
          icon: 'text-gray-500'
        },
        {
          key: 'not-received',
          label: 'Not Received',
          hint: 'Item was not in the delivery',
          bg: 'bg-gray-300 hover:bg-gray-400 active:bg-gray-400',
          text: 'text-gray-600',
          icon: 'text-gray-400'
        }
      ]
    },

    currentReasonLabel() {
      const r = this.reasons.find((r) => r.key === this.selectedReason)
      return r ? r.label : ''
    },

    requiresPhotos() {
      return ['damaged', 'wrong-item'].includes(this.selectedReason)
    },

    actionForReason() {
      const map = {
        'wrong-qty': 'Discrepancy: Wrong Number Received',
        damaged: 'Returned: Damaged / Poor Quality',
        'wrong-item': 'Returned: Incorrect Product',
        'not-received': 'Not Received'
      }
      return map[this.selectedReason] || 'Received'
    }
  },
  methods: {
    save() {
      const inv = this.invoiceById(this.invoiceId)
      const existing = (inv && inv[this.productId]) || {}
      const photos = []
      if (this.productPhotoUrl)
        photos.push({ url: this.productPhotoUrl, type: 'product' })

      let amountReceived = this.amountReceived
      let amountBilled
      let amountReturned = this.amountReturned

      if (this.selectedReason === 'not-received') {
        amountReceived = 0
        amountBilled = 0
        amountReturned = 0
      } else if (this.requiresPhotos) {
        amountReceived = existing.amountOrdered || 0
        amountBilled = Math.max(
          0,
          (existing.amountOrdered || 0) - (amountReturned || 0)
        )
      } else {
        amountBilled = amountReceived
      }

      const itemData = {
        ...existing,
        action: this.actionForReason,
        amountReceived: amountReceived || 0,
        amountBilled:
          amountBilled !== null ? amountBilled : amountReceived || 0,
        amountReturned: amountReturned || 0,
        returnReason: this.returnReason || null,
        photos
      }
      if (this.selectedReason === 'wrong-item') {
        itemData.wrongItemDescription = this.wrongItemDescription || null
      }
      this.$emit('save', { productId: this.productId, itemData })
    }
  }
}
</script>
